import type { RefObject, TouchEvent as ReactTouchEvent } from 'react';
import { useRef } from 'react';

/**
 * 1.18 — two fingers on a scrolling box, read as a zoom.
 *
 * Asked for by Nitzan for the document being marked up, to place a signature or
 * a text exactly, and already there in the invoice preview (INV), which is where
 * this came from. The two share it, so a phone zooms a page the same way
 * wherever it is shown.
 *
 * Buttons are the whole zoom on a desktop, and on a phone they would be the only
 * one, which is not how anyone reads a page: the gesture a phone teaches is a
 * pinch. The browser's own pinch is no help here, because it scales the screen
 * and not the box, so `touch-action: pan-x pan-y` on the scroll box takes two
 * fingers away from it, and leaves it the one that scrolls. This reads the two.
 *
 * Touch events, not pointer events, on purpose. When the browser takes a pan
 * over it cancels the pointers it took, and two fingers that drift a little as
 * they spread would end a pinch in the middle of it; touches keep arriving while
 * the browser scrolls. React registers `touchmove` passively, so nothing here
 * calls `preventDefault` — `touch-action` is what holds the browser off.
 *
 * Live feedback is a CSS scale on `target`, which costs nothing per frame. When
 * a finger lifts, `onEnd` is handed the zoom to settle on and the caller lays
 * the pages out again at it, sharp rather than an upscaled bitmap. `onEnd` runs
 * while the scale is still on `target`, so a caller who wants to know where
 * things are on the screen can measure them as the fingers left them; the scale
 * comes off as soon as it returns.
 */

/** The width that fits the box, and the furthest a pinch takes the pages from it. */
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;

interface Finger {
  clientX: number;
  clientY: number;
}

/** How far apart the first two fingers are, in pixels; nothing, with fewer than two. */
export function spread(touches: ArrayLike<Finger | undefined>): number {
  const a = touches[0];
  const b = touches[1];
  if (!a || !b) return 0;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

/**
 * How much the pages are scaled by while two fingers are down: how far apart
 * they are against where they started, from wherever the zoom already is.
 *
 * Clamped here, while the fingers move, rather than on release, so the pages
 * stop growing at the same place the zoom stops: a pinch that ran past the top
 * and then sprang back reads as a gesture that was refused.
 */
export function pinchRatio(start: number, now: number, zoom: number, min: number, max: number): number {
  if (start <= 0) return 1;
  return Math.min(Math.max(now / start, min / zoom), max / zoom);
}

/**
 * The zoom a release settles on. With steps, the nearest one, and the lower at a
 * tie; without, wherever the fingers left it, inside the limits.
 */
export function settledZoom(
  zoom: number,
  ratio: number,
  min: number,
  max: number,
  steps?: readonly number[],
): number {
  const wanted = zoom * ratio;
  if (!steps?.length) return Math.min(Math.max(wanted, min), max);
  return steps.reduce((best, step) => (Math.abs(step - wanted) < Math.abs(best - wanted) ? step : best), steps[0]!);
}

interface PinchZoomOptions {
  /** Where the zoom is now: 1 is the width that fits the box. */
  zoom: number;
  min: number;
  max: number;
  /** The levels a release settles on. Without them, it settles where the fingers left it. */
  steps?: readonly number[];
  /** The element scaled while the fingers move. */
  target: RefObject<HTMLElement | null>;
  /** A second finger came down. `centre` is the point between the two, on the screen. */
  onStart?: (centre: { x: number; y: number }) => void;
  /** A finger came up: the zoom to settle on, which is the one it was if the pinch got nowhere. */
  onEnd: (zoom: number) => void;
}

/** The touch handlers for the scroll box the pages are in. */
export function usePinchZoom({ zoom, min, max, steps, target, onStart, onEnd }: PinchZoomOptions) {
  const pinch = useRef<{ start: number; ratio: number } | null>(null);

  /* Both fingers have to be on the box. A dialog opened from it is drawn
     elsewhere on the screen, but React still hands its touches up through the
     box, and drawing a name with two fingers must not zoom the pages behind. */
  const onBox = (event: ReactTouchEvent<HTMLElement>) =>
    [0, 1].every((at) => {
      const finger = event.touches[at];
      return finger !== undefined && event.currentTarget.contains(finger.target as Node);
    });

  const end = () => {
    const state = pinch.current;
    if (!state) return;
    pinch.current = null;
    onEnd(settledZoom(zoom, state.ratio, min, max, steps));
    if (target.current) target.current.style.transform = '';
  };

  return {
    onTouchStart: (event: ReactTouchEvent<HTMLElement>) => {
      const a = event.touches[0];
      const b = event.touches[1];
      if (event.touches.length !== 2 || !a || !b || !onBox(event)) return;
      pinch.current = { start: spread(event.touches), ratio: 1 };
      onStart?.({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });
    },
    onTouchMove: (event: ReactTouchEvent<HTMLElement>) => {
      const state = pinch.current;
      if (!state || event.touches.length !== 2 || state.start === 0) return;
      state.ratio = pinchRatio(state.start, spread(event.touches), zoom, min, max);
      if (target.current) target.current.style.transform = `scale(${state.ratio})`;
    },
    onTouchEnd: end,
    onTouchCancel: end,
  };
}
